import React, { useEffect, useState } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import AuctionCard from '../../customComponents/AuctionCard';
import LotDescription from '../../components/LotDescription';
import LiveStartTimeInfo from '../../components/common/LiveStartTimeInfo';
import AuctionHouseComponent from '../../components/common/AuctionHouse';
import ImageView from '../../components/ImageView';
import LiveBadge from '../../customComponents/LiveBadge';
import FavoriteIcon from '../../components/common/FavoriteIcon';
import { getAuction } from '../../lib/api/user/userApi';
import { AuctionDetailData } from '../../lib/api/user/userApi.types';
import { formatLiveTime, formatAuctionPeriod } from '../../utils/dateUtils';
import { isShoppingMode } from '../../config/appMode';

// 웹용 제목 컴포넌트 (FormTitle과 동일한 스타일)
const WebTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: left;
  margin: 0 0 20px 0;
  
  @media (max-width: 767px) {
    display: none; /* 모바일에서는 숨김 */
  }
`;

// 컨테이너
const AuctionIntroContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: calc(100vh - 120px);
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: left;
  margin: 0 0 16px 0;
`;

// ListSection과 동일한 카드 스타일
const ListSectionCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: visible;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  width: 100%;
  position: relative;

  &:hover {
    transform: translateY(-4px);
  }
`;

const BadgeContainer = styled.div`
  width: auto;
  margin-bottom: 8px;
  display: flex;
  justify-content: flex-start;
`;

const CardImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  position: relative;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  padding: 16px;
  box-sizing: border-box;
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 12px 16px;
  box-sizing: border-box;
  border-radius: 8px;
`;

const EndedAuctionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EndedAuctionText = styled.span`
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.5px;
`;

const OverlayDateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 4px;
`;

const OverlayDateLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
`;

const OverlayDateValue = styled.span`
  font-size: 12px;
  color: #ffffff;
  font-weight: 500;
`;

const DesktopOverlayDateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 6px;
`;

const DesktopOverlayDateLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
`;

const DesktopOverlayDateValue = styled.span`
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
`;

const CardContent = styled.div`
  padding: 16px;
  text-align: left;
`;

const AuctionTitle = styled.div`
  font-size: 20px;
  color: #000;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const LotName = styled.h3`
  font-size: 16px;
  color: #000;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
`;

const PriceInfo = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceLabel = styled.span`
  font-size: 12px;
  color: #999;
  font-weight: 400;
`;

const PriceValue = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 600;
`;

const AuctionCardContainer = styled.div`
  width: 100%;
  
  @media (min-width: 768px) {
    width: 100%;
  }
`;

const LotListGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
`;

// 모바일용 2줄 그리드
const MobileLotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

// 데스크탑용 3줄 그리드
const DesktopLotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #999;
  text-align: center;
`;

interface LocationState {
  auctionData?: any;
  selectedLotId?: number;
}

// 다국어 텍스트
const getAuctionIntroTexts = (lang: 'ko' | 'en') => {
  const shopping = isShoppingMode();
  
  if (lang === 'en') {
    return {
      title: shopping ? 'Product Introduction' : 'Auction Introduction',
      auctionLot: shopping ? 'Product' : 'Auction Item',
      liveInfo: 'Live Information',
      description: shopping ? 'Product Description' : 'Auction Description',
      lotList: shopping ? 'Products in this Sale' : 'LOTs in this Auction',
      loading: 'Loading...',
      error: 'Unable to load data.',
      photoSubmissionDate: 'Photo Bid Date',
      auctionDate: 'Auction Period',
      auctionStart: shopping ? 'Sale Start' : 'Auction Start',
      auctionEnd: shopping ? 'Sale End' : 'Auction End',
      startingPrice: shopping ? 'Sale Price' : 'Starting Price',
      auctionEnded: shopping ? 'Sale Ended' : 'Auction Ended'
    };
  }
  
  return {
    title: shopping ? '상품 소개' : '경매 소개',
    auctionLot: shopping ? '상품' : '경매품',
    liveInfo: '라이브 정보',
    description: shopping ? '상품 설명' : '경매 설명',
    lotList: shopping ? '이 판매의 상품' : '이 경매의 LOT',
    loading: '로딩 중...',
    error: '데이터를 불러올 수 없습니다.',
    photoSubmissionDate: '사진입찰',
    auctionDate: '경매 일시',
    auctionStart: shopping ? '판매 시작' : '경매 시작',
    auctionEnd: shopping ? '판매 종료' : '경매 종료',
    startingPrice: shopping ? '판매가' : '시작가',
    auctionEnded: shopping ? '판매종료' : '경매종료'
  };
};

const AuctionIntroPage: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useLang();
  const { user } = useUserAuth();
  const texts = getAuctionIntroTexts(lang);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auctionDetail, setAuctionDetail] = useState<AuctionDetailData | null>(null);
  const [selectedLotId, setSelectedLotId] = useState<number | null>(null);

  // 사용자가 해당 LOT을 좋아요했는지 확인하는 함수
  const isLikedByUser = (likedUserIndexes: number[] | null) => {
    if (!user?.index || !likedUserIndexes || !Array.isArray(likedUserIndexes)) {
      return false;
    }
    return likedUserIndexes.includes(user.index);
  };

  // 더미 좋아요 클릭 핸들러 (실제 기능은 구현하지 않음)
  const handleLikeClick = (e: React.MouseEvent, lotId: number) => {
    e.stopPropagation();
    devLog('Like clicked for lot:', lotId);
  };

  // 라우터 state에서 데이터 가져오기 (이전 버전과의 호환성)
  const state = location.state as LocationState;
  const legacyAuctionData = state?.auctionData;

  // res.json 형태의 작품 데이터를 경매 데이터로 변환하는 함수  
  const convertLotDataToAuction = (lotData: any, auctionData: AuctionDetailData) => {
    return {
      id: auctionData.index,
      name: auctionData.name,
      auctioneer: auctionData.auctioneer,
      preBidStartAt: auctionData.preBidStartAt,
      preBidEndAt: auctionData.preBidEndAt,
      auctionStartAt: auctionData.auctionStartAt,
      auctionEndAt: auctionData.auctionEndAt,
      lots: [
        {
          images: lotData.images || []
        }
      ],
      status: 'ongoing'
    };
  };

  // 카드 클릭 핸들러
  const handleCardClick = (auction: any) => {
    // 경매 ID와 첫 번째 작품 정보로 lot-detail 페이지로 이동
    if (auctionDetail && auctionDetail.lots && auctionDetail.lots.length > 0) {
      const auctionId = auctionDetail.index;
      navigate(`/lot-detail/${auctionId}`);
    }
  };

  // 경매 상세 데이터 로드
  useEffect(() => {
    const fetchAuctionDetail = async () => {
      if (!auctionId) {
        setError('경매 ID가 없습니다.');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getAuction(auctionId);
        
        if (response.data.statusCode === 200 && response.data.data) {
          setAuctionDetail(response.data.data);
          // 첫 번째 작품을 기본 선택으로 설정
          if (response.data.data.lots && response.data.data.lots.length > 0) {
            setSelectedLotId(response.data.data.lots[0].index);
          }
        } else {
          setError(response.data.message || '경매 정보를 불러올 수 없습니다.');
        }
      } catch (error) {
        devError('API 호출 오류:', error);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchAuctionDetail();
  }, [auctionId]);
  
  // 현재 선택된 작품을 경매 데이터로 변환
  const currentAuction = auctionDetail && selectedLotId ? 
    (() => {
      const selectedLot = auctionDetail.lots?.find(lot => lot.index === selectedLotId);
      return selectedLot ? convertLotDataToAuction(selectedLot, auctionDetail) : null;
    })() : null;

  // 뒤로가기 핸들러
  const handleBack = () => {
    navigate(-1);
  };

  if (!auctionId && !legacyAuctionData) {
    return (
      <MainLayout>
        <CenteredLayout>
          <ProjectAppbar 
            title={texts.title}
            showBackButton={true}
            onBack={handleBack}
          />
          <AuctionIntroContainer>
            <ContentContainer>
              <ErrorContainer>
                경매 정보가 없습니다.
              </ErrorContainer>
            </ContentContainer>
          </AuctionIntroContainer>
        </CenteredLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <CenteredLayout>
        <ProjectAppbar 
          title={texts.title}
          showBackButton={true}
          onBack={handleBack}
        />
        <AuctionIntroContainer>
          <ContentContainer>
            <WebTitle>{texts.title}</WebTitle>
            {/* 선택된 경매품 카드 */}
            {currentAuction && (
              <Section>
                <AuctionCardContainer>
                  <AuctionCard
                    auction={currentAuction}
                    lang={lang}
                    type="ongoing"
                    onClick={handleCardClick}
                  />
                </AuctionCardContainer>
              </Section>
            )}

            {/* 라이브 시작 시간 정보 - 쇼핑 모드에서는 숨김 */}
            {currentAuction && !isShoppingMode() && (
              <Section>
                {/* <SectionTitle>{texts.liveInfo}</SectionTitle> */}
                <LiveStartTimeInfo
                  liveStartTime={currentAuction.auctionStartAt}
                  liveEndTime={currentAuction.auctionEndAt}
                />
              </Section>
            )}

            {/* 경매 설명 - 쇼핑 모드에서는 숨김 */}
            {auctionDetail?.description && !isShoppingMode() && (
              <Section>
                <LotDescription
                  title={texts.description}
                  description={auctionDetail.description}
                  isVisible={true}
                />
              </Section>
            )}

            {/* LOT 리스트 */}
            {auctionDetail?.lots && auctionDetail.lots.length > 0 && (
              <Section>
                <SectionTitle>{texts.lotList}</SectionTitle>
                <ResponsiveView
                  mobileView={
                    <MobileLotGrid>
                      {auctionDetail.lots.map((lot) => {
                        const now = new Date();
                        const auctionStartAt = new Date(auctionDetail.auctionStartAt);
                        const auctionEndAt = new Date(auctionDetail.auctionEndAt);
                        const preBidStartAt = new Date(auctionDetail.preBidStartAt);
                        
                        const isEnded = now > auctionEndAt;
                        
                        // 사진입찰 시간이 현재 시간 이전인지 체크
                        const shouldShowOverlay = preBidStartAt > now;
                        
                        // 이미지 URL 구성
                        const imageUrl = lot.images && lot.images.length > 0 ? 
                          (lot.images[0].startsWith('http') ? 
                            lot.images[0] : 
                            `${process.env.REACT_APP_API_HOST || 'http://localhost:3001'}/${lot.images[0]}`) : 
                          '';

                        return (
                          <ListSectionCard key={lot.index} onClick={() => handleCardClick(lot.index)}>
                            {/* 종료된 경매가 아닐 때만 라이브 뱃지 표시 (쇼핑모드에서는 숨김) */}
                            {!isEnded && !isShoppingMode() && (
                              <BadgeContainer>
                                <LiveBadge 
                                  liveTime={auctionStartAt ? formatLiveTime(auctionStartAt.toISOString(), lang, auctionEndAt.toISOString()) : '예정'} 
                                  isMobile={true}
                                />
                              </BadgeContainer>
                            )}
                            
                            <CardImageContainer>
                              <ImageView
                                imageUrl={imageUrl}
                                width="100%"
                                height="100%"
                                borderRadius={0}
                                alt={lot.title || 'Lot'}
                                objectFit="contain"
                              />
                              
                              {/* 종료된 경매일 때 전체 반투명 오버레이 */}
                              {isEnded && (
                                <EndedAuctionOverlay>
                                  <EndedAuctionText>{texts.auctionEnded}</EndedAuctionText>
                                </EndedAuctionOverlay>
                              )}
                              
                              {/* 좋아요 아이콘 (이미지 안의 오른쪽 상단) */}
                              <FavoriteIcon 
                                isFavorite={isLikedByUser([lot.index])} 
                                onClick={(e) => handleLikeClick(e, lot.index)}
                              />
                              
                              {/* 이미지 하단 오버레이 - 사진입찰 시간이 현재 시간 이후이고 종료되지 않은 경매일 때만 표시 */}
                                {shouldShowOverlay && !isEnded && !isShoppingMode() && (
                                <ImageOverlay>
                                  <>
                                    <OverlayDateInfo>
                                      <OverlayDateLabel>{texts.photoSubmissionDate}</OverlayDateLabel>
                                      <OverlayDateValue>{new Date(auctionDetail.preBidStartAt).toLocaleDateString('ko-KR', { 
                                        year: '2-digit', 
                                        month: '2-digit', 
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }).replace(/\//g, '.').replace(', ', ' ')}</OverlayDateValue>
                                    </OverlayDateInfo>
                                    
                                    <OverlayDateInfo>
                                      <OverlayDateLabel>{texts.auctionStart}</OverlayDateLabel>
                                      <OverlayDateValue>{formatAuctionPeriod(auctionDetail.auctionStartAt, auctionDetail.auctionEndAt).split(' ~ ')[0]}</OverlayDateValue>
                                    </OverlayDateInfo>
                                    
                                    <OverlayDateInfo>
                                      <OverlayDateLabel>{texts.auctionEnd}</OverlayDateLabel>
                                      <OverlayDateValue>{formatAuctionPeriod(auctionDetail.auctionStartAt, auctionDetail.auctionEndAt).split(' ~ ')[1]}</OverlayDateValue>
                                    </OverlayDateInfo>
                                  </>
                                </ImageOverlay>
                              )}
                            </CardImageContainer>
                            
                            <CardContent>
                              <AuctionHouseComponent 
                                auctioneerName={auctionDetail.auctioneer?.name || '경매사'}
                                auctioneerIndex={auctionDetail.auctioneer?.index || 0}
                              />
                              <AuctionTitle>{lot.title}</AuctionTitle>
                              <LotName>{lot.lotCode || `LOT.${lot.index}`}</LotName>
                              
                              {/* 가격 정보 */}
                              <PriceInfo>
                                <PriceItem>
                                  <PriceLabel>{texts.startingPrice}</PriceLabel>
                                  <PriceValue>
                                    {lang === 'ko' 
                                      ? `KRW ${lot.startPrice?.toLocaleString() || '0'}`
                                      : `USD ${(lot.startPrice / 1300)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`
                                    }
                                  </PriceValue>
                                </PriceItem>
                              </PriceInfo>
                            </CardContent>
                          </ListSectionCard>
                        );
                      })}
                    </MobileLotGrid>
                  }
                  desktopView={
                    <DesktopLotGrid>
                      {auctionDetail.lots.map((lot) => {
                        const now = new Date();
                        const auctionStartAt = new Date(auctionDetail.auctionStartAt);
                        const auctionEndAt = new Date(auctionDetail.auctionEndAt);
                        const preBidStartAt = new Date(auctionDetail.preBidStartAt);
                        
                        const isEnded = now > auctionEndAt;
                        
                        // 사진입찰 시간이 현재 시간 이전인지 체크
                        const shouldShowOverlay = preBidStartAt > now;
                        
                        // 이미지 URL 구성
                        const imageUrl = lot.images && lot.images.length > 0 ? 
                          (lot.images[0].startsWith('http') ? 
                            lot.images[0] : 
                            `${process.env.REACT_APP_API_HOST || 'http://localhost:3001'}/${lot.images[0]}`) : 
                          '';

                        return (
                          <ListSectionCard key={lot.index} onClick={() => handleCardClick(lot.index)}>
                            {/* 종료된 경매가 아닐 때만 라이브 뱃지 표시 (쇼핑모드에서는 숨김) */}
                            {!isEnded && !isShoppingMode() && (
                              <BadgeContainer>
                                <LiveBadge 
                                  liveTime={auctionStartAt ? formatLiveTime(auctionStartAt.toISOString(), lang, auctionEndAt.toISOString()) : '예정'} 
                                  isMobile={false}
                                />
                              </BadgeContainer>
                            )}
                            
                            <CardImageContainer>
                              <ImageView
                                imageUrl={imageUrl}
                                width="100%"
                                height="100%"
                                borderRadius={0}
                                alt={lot.title || 'Lot'}
                                objectFit="contain"
                              />
                              
                              {/* 종료된 경매일 때 전체 반투명 오버레이 */}
                              {isEnded && (
                                <EndedAuctionOverlay>
                                  <EndedAuctionText>{texts.auctionEnded}</EndedAuctionText>
                                </EndedAuctionOverlay>
                              )}
                              
                              {/* 좋아요 아이콘 (이미지 안의 오른쪽 상단) */}
                              <FavoriteIcon 
                                isFavorite={isLikedByUser([lot.index])} 
                                onClick={(e) => handleLikeClick(e, lot.index)}
                              />
                              
                              {/* 이미지 하단 오버레이 - 사진입찰 시간이 현재 시간 이후이고 종료되지 않은 경매일 때만 표시 (쇼핑 모드에서는 숨김) */}
                              {shouldShowOverlay && !isEnded && !isShoppingMode() && (
                                <ImageOverlay>
                                  <>
                                    <DesktopOverlayDateInfo>
                                      <DesktopOverlayDateLabel>{texts.photoSubmissionDate}</DesktopOverlayDateLabel>
                                      <DesktopOverlayDateValue>{new Date(auctionDetail.preBidStartAt).toLocaleDateString('ko-KR', { 
                                        year: '2-digit', 
                                        month: '2-digit', 
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }).replace(/\//g, '.').replace(', ', ' ')}</DesktopOverlayDateValue>
                                    </DesktopOverlayDateInfo>
                                    
                                    <DesktopOverlayDateInfo>
                                      <DesktopOverlayDateLabel>{texts.auctionDate}</DesktopOverlayDateLabel>
                                      <DesktopOverlayDateValue>{formatAuctionPeriod(auctionDetail.auctionStartAt, auctionDetail.auctionEndAt)}</DesktopOverlayDateValue>
                                    </DesktopOverlayDateInfo>
                                  </>
                                </ImageOverlay>
                              )}
                            </CardImageContainer>
                            
                            <CardContent>
                              <AuctionHouseComponent 
                                auctioneerName={auctionDetail.auctioneer?.name || '경매사'}
                                auctioneerIndex={auctionDetail.auctioneer?.index || 0}
                              />
                              <AuctionTitle>{lot.title}</AuctionTitle>
                              {!isShoppingMode() && (
                                <LotName>{lot.lotCode || `LOT.${lot.index}`}</LotName>
                              )}
                              
                              {/* 가격 정보 */}
                              <PriceInfo>
                                <PriceItem>
                                  <PriceLabel>{texts.startingPrice}</PriceLabel>
                                  <PriceValue>
                                    {lang === 'ko' 
                                      ? `KRW ${lot.startPrice?.toLocaleString() || '0'}`
                                      : `USD ${(lot.startPrice / 1300)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`
                                    }
                                  </PriceValue>
                                </PriceItem>
                              </PriceInfo>
                            </CardContent>
                          </ListSectionCard>
                        );
                      })}
                    </DesktopLotGrid>
                  }
                />
              </Section>
            )}

            {/* 로딩 및 에러 상태 */}
            {loading && (
              <LoadingContainer>{texts.loading}</LoadingContainer>
            )}
            
            {error && (
              <ErrorContainer>{error}</ErrorContainer>
            )}
          </ContentContainer>
        </AuctionIntroContainer>
      </CenteredLayout>
    </MainLayout>
  );
};

export default AuctionIntroPage;
